import { useEffect, useRef } from 'react';

const NORMAL_WIDGET_WIDTH = 300;
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      size?: 'normal' | 'flexible' | 'compact';
    },
  ) => string;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

interface TurnstileProps {
  onToken: (token: string) => void;
}

function siteKey(): string {
  return (import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '') as string;
}

function loadScript(): void {
  if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;
  const script = document.createElement('script');
  script.src = SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

/**
 * Cloudflare Turnstile widget. When the site key is configured it loads the
 * Cloudflare script and renders the challenge, calling `onToken` with the
 * verification token on success. When the site key is empty (local dev / test)
 * it renders a "Skip verification (dev)" button so the signup flow stays
 * exercisable without a real Turnstile deployment.
 */
export function Turnstile({ onToken }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const key = siteKey();

  useEffect(() => {
    if (!key) return;
    loadScript();

    let rendered = false;
    const tryRender = (): boolean => {
      const container = containerRef.current;
      if (rendered || !container || !window.turnstile) return false;
      // The normal widget is a fixed 300px iframe. A container narrower than
      // that (375px phones inside the padded card) would be overflowed and
      // skew the card's gutters, so fall back to the compact widget there.
      // A width of 0 means "not measurable" (hidden / test DOM): use flexible.
      const width = container.clientWidth;
      const size = width > 0 && width < NORMAL_WIDGET_WIDTH ? 'compact' : 'flexible';
      window.turnstile.render(container, { sitekey: key, callback: onToken, size });
      rendered = true;
      return true;
    };

    if (tryRender()) return;
    const interval = window.setInterval(() => {
      if (tryRender()) window.clearInterval(interval);
    }, 200);
    return () => window.clearInterval(interval);
  }, [key, onToken]);

  if (!key) {
    return (
      <button
        type="button"
        onClick={() => onToken('dev-token')}
        className="rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50"
      >
        Skip verification (dev)
      </button>
    );
  }

  return <div ref={containerRef} data-testid="turnstile-widget" className="w-full min-w-0" />;
}
